import React, { useState, useEffect, useCallback, useRef } from "react";
import Divider from "@mui/material/Divider";
import { Sidebar } from "primereact/sidebar";
import { TabView, TabPanel } from "primereact/tabview";
import { InputTextarea } from "primereact/inputtextarea";
import { Paginator } from "primereact/paginator";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import notificationScreens from "../../services/profileScreen/notificationScreens";
import PatientSearch from "../../components/Shared/patientSearch";
import blueline from "../../assets/images/blueline.svg";
import search from "../../assets/images/search.svg";
import PropTypes from 'prop-types';

const PAGE_SIZE = 5;
const TAB_KEYS = {
  RECEIVED: "received",
  SENT: "sent",
  NEW_NOTIFICATION: "newNotification"
};

const Notifications = ({ show, setShow }) => {
  const toastRef = useRef(null);
  
  const [notificationData, setNotificationData] = useState([]);
  const [sendNotification, setSendNotification] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [liveTime, setLiveTime] = useState("");
  const [patientSearch, setPatientSearch] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPagesent, setCurrentPagesent] = useState(0);
  
  const [fromdate, setFromdate] = useState(null);
  const [todate, setTodate] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  
  const [tabSearchItems, setTabSearchItems] = useState({
    [TAB_KEYS.RECEIVED]: null,
    [TAB_KEYS.SENT]: null,
    [TAB_KEYS.NEW_NOTIFICATION]: null,
  });

  const userObj = JSON.parse(localStorage.getItem("loginObj") || "{}");
  const user = userObj.user || {};
  const testParam = user.code;
  const toName = `${user.salutation || ""} ${user.firstName || ""} ${user.lastName || ""}`.trim();

  const showToast = useCallback((severity, summary, message) => {
    toastRef.current?.show({
      severity,
      summary,
      detail: message,
      life: 3000,
    });
  }, []);

  const showSuccess = useCallback((message) => {
    showToast("success", "Success", message);
  }, [showToast]);

  const showError = useCallback((message) => {
    showToast("error", "Error", message);
  }, [showToast]);

  const showInfo = useCallback((message) => {
    showToast("info", "Info", message);
  }, [showToast]);

  const togglePatientSearchbar = useCallback(() => {
    setPatientSearch(prev => !prev);
  }, []);

  const handleSelectedData = useCallback((selectedData) => {
    const tabKeys = Object.values(TAB_KEYS);
    const activeTabKey = tabKeys[activeIndex];
    
    if (activeTabKey) {
      setTabSearchItems(prev => ({
        ...prev,
        [activeTabKey]: selectedData,
      }));
    }
  }, [activeIndex]);

  const formatDate = useCallback((date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }, []);

  const getCurrentTime = useCallback(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const date = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${date} ${hours}:${minutes}:${seconds}`;
  }, []);

  const onSearch = useCallback(async (activeTabName) => {
    setLoading(true);
    
    try {
      const payload = {};
      
      if (activeTabName === TAB_KEYS.RECEIVED) {
        payload.trigeredTo = tabSearchItems[TAB_KEYS.RECEIVED]?.code || testParam;
      } else if (activeTabName === TAB_KEYS.SENT) {
        payload.trigeredBy = tabSearchItems[TAB_KEYS.SENT]?.code || testParam;
      }

      if (fromdate && todate) {
        payload.fromDate = formatDate(fromdate);
        payload.toDate = formatDate(todate);
      }

      const res = await notificationScreens.getNotificationSend(payload);
      
      if (res?.data?.successList) {
        if (activeTabName === TAB_KEYS.RECEIVED) {
          setNotificationData(res.data.successList);
        } else if (activeTabName === TAB_KEYS.SENT) {
          setSendNotification(res.data.successList);
        }
        
        if (res.data.successList.length === 0) {
          showInfo("No Data Found");
        }
      }
    } catch (error) {
      console.error("Search error:", error);
      showError("Server Error");
    } finally {
      setLoading(false);
    }
  }, [fromdate, todate, tabSearchItems, testParam, formatDate, showInfo, showError]);

  const sendNotificationHandler = useCallback(async () => {
    const activeSearchItem = tabSearchItems[TAB_KEYS.NEW_NOTIFICATION];
    
    if (!activeSearchItem?.code) {
      showError("Please select a recipient");
      return;
    }

    if (!subject.trim()) {
      showError("Subject is required");
      return;
    }

    if (!body.trim()) {
      showError("Message body is required");
      return;
    }

    setLoading(true);

    const payload = {
      userCode: testParam,
      patientName: activeSearchItem.name,
      patientCode: activeSearchItem.code,
      date: liveTime,
      subject: subject.trim(),
      content: body.trim(),
      status: "1",
      createdDate: liveTime,
      fromDate: liveTime,
      updatedDate: liveTime,
      createdBy: testParam,
      updatedBy: testParam,
      trigeredBy: testParam,
      trigeredTo: activeSearchItem.code,
      trigeredUsertype: "P",
      fromName: toName,
    };

    try {
      const res = await notificationScreens.sendnotification(payload);
      if (res?.data?.responseCode === '200') {
        setSubject("");
        setBody("");
        setShow(false);
        setTabSearchItems(prev => ({ ...prev, [TAB_KEYS.NEW_NOTIFICATION]: null }));
        showSuccess("Notification Sent Successfully");
      } else {
        showError(res?.data?.message || "Failed to send notification");
      }
    } catch (error) {
      let errorMessage = "Error sending notification";
      if (error.response?.data) {
        const errorData = error.response.data;
        errorMessage = errorData.message || errorMessage;
        if (errorData.details?.[1]) {
          errorMessage += `: ${errorData.details[1]}`;
        }
      }
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [tabSearchItems, subject, body, liveTime, testParam, toName, setShow, showSuccess, showError]);

  const onPageChange = useCallback((event) => {
    setCurrentPage(event.page);
  }, []);

  const onPageChangeSent = useCallback((event) => {
    setCurrentPagesent(event.page);
  }, []);

  const onTabChange = useCallback((e) => {
    setActiveIndex(e.index);
    setFromdate(null);
    setTodate(null);
  }, []);

  const getPaginatedData = useCallback((data, currentPageIndex) => {
    const startIndex = currentPageIndex * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return data.slice(startIndex, endIndex);
  }, []);

  const getMailData = getPaginatedData(notificationData, currentPagesent);
  const currentData = getPaginatedData(sendNotification, currentPage);

  const formatDisplayDate = useCallback((dateString) => {
    if (!dateString) return "";
    return dateString.substring(0, 10);
  }, []);

  useEffect(() => {
    let intervalId;
    if (show) {
      const updateTime = () => {
        setLiveTime(getCurrentTime());
      };
      updateTime();
      intervalId = setInterval(updateTime, 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [show, getCurrentTime]);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      togglePatientSearchbar();
    }
  }, [togglePatientSearchbar]);

  const handleSearchClick = useCallback(() => {
    togglePatientSearchbar();
  }, [togglePatientSearchbar]);

  const renderSearchSection = useCallback((tabKey, placeholder) => (
    <div className="flex flex-row items-center relative w-full mb-6 overflow-hidden">
      <InputText
        value={tabSearchItems[tabKey]?.name || ""}
        placeholder={placeholder}
        className="border rounded border-solid border-[#E2E2E2] box-border w-full pl-3 pr-10 cursor-pointer"
        onClick={handleSearchClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleSearchClick();
          }
        }}
        readOnly
      />
      <button
        className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-transparent border-none p-0 cursor-pointer"
        onClick={handleSearchClick}
        onKeyDown={handleSearchKeyDown}
        aria-label="Search patient"
        type="button"
      >
        <img
          src={search}
          alt=""
          aria-hidden="true"
          className="w-4 h-4"
        />
      </button>
    </div>
  ), [tabSearchItems, handleSearchClick, handleSearchKeyDown]);

  const renderDateFilters = useCallback(() => (
    <div className="flex flex-wrap gap-4 mb-6">
      <Calendar
        value={fromdate}
        placeholder="Enter From Date"
        onChange={(e) => setFromdate(e.value)}
        showIcon
        className="border rounded border-solid border-[#E2E2E2] box-border w-40"
        maxDate={todate || new Date()}
      />
      <Calendar
        value={todate}
        placeholder="Enter To Date"
        onChange={(e) => setTodate(e.value)}
        showIcon
        className="border rounded border-solid border-[#E2E2E2] box-border w-40"
        minDate={fromdate}
        maxDate={new Date()}
      />
      <button
        className="w-40 h-10 border border-[#004172] bg-[#004172] text-white font-medium rounded-md hover:bg-[#00315a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => onSearch(activeIndex === 0 ? TAB_KEYS.RECEIVED : TAB_KEYS.SENT)}
        disabled={loading}
      >
        {loading ? "Searching..." : "Search"}
      </button>
      <button
        className="w-40 h-10 border border-[#004172] bg-white text-[#004172] font-medium rounded-md hover:bg-gray-50 transition-colors"
        onClick={() => globalThis.print()}
      >
        Print
      </button>
    </div>
  ), [fromdate, todate, loading, activeIndex, onSearch]);

  const renderNotificationItem = useCallback((data, index) => (
    <div
      className="inline-block rounded-xl bg-white border border-gray-300 mb-4 w-full overflow-hidden"
      key={data.id || index}
    >
      <div className="flex flex-row items-start">
        <img src={blueline} alt="" aria-hidden="true" className="h-full" />
        <div className="flex flex-col ml-4 p-3 w-full">
          <h1 className="font-bold mb-2 text-lg">{data.subject || "No Subject"}</h1>
          <p className="text-sm mb-3">{data.content || "No content"}</p>
          <Divider />
          <div className="text-sm mt-3 text-gray-600 flex items-center gap-2">
            <i className="pi pi-calendar" aria-hidden="true"></i>
            {formatDisplayDate(data.date)}
          </div>
        </div>
      </div>
    </div>
  ), [formatDisplayDate]);

  const renderReceivedTab = useCallback(() => (
    <>
      {renderSearchSection(TAB_KEYS.RECEIVED, "Search recipient...")}
      {renderDateFilters()}
      
      {getMailData.length > 0 ? (
        <>
          {getMailData.map(renderNotificationItem)}
          <Paginator
            first={currentPagesent * PAGE_SIZE}
            rows={PAGE_SIZE}
            totalRecords={notificationData.length}
            onPageChange={onPageChangeSent}
            className="mt-4"
          />
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          {loading ? "Loading..." : "No notifications found"}
        </div>
      )}
    </>
  ), [getMailData, notificationData.length, currentPagesent, onPageChangeSent, loading, renderSearchSection, renderDateFilters, renderNotificationItem]);

  const renderSentTab = useCallback(() => (
    <>
      {renderSearchSection(TAB_KEYS.SENT, "Search recipient...")}
      {renderDateFilters()}
      
      {currentData.length > 0 ? (
        <>
          {currentData.map(renderNotificationItem)}
          <Paginator
            first={currentPage * PAGE_SIZE}
            rows={PAGE_SIZE}
            totalRecords={sendNotification.length}
            onPageChange={onPageChange}
            className="mt-4"
          />
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          {loading ? "Loading..." : "No sent notifications found"}
        </div>
      )}
    </>
  ), [currentData, sendNotification.length, currentPage, onPageChange, loading, renderSearchSection, renderDateFilters, renderNotificationItem]);

  const handleInputTextKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSearchClick();
    }
  }, [handleSearchClick]);

  const renderNewNotificationTab = useCallback(() => (
    <>
      <div className="flex flex-col">
        <label htmlFor="from-input" className="block text-sm font-medium text-black mb-4">
          From
        </label>
        <InputText
          id="from-input"
          value={toName}
          disabled
          placeholder="From...."
          className="border rounded border-solid border-[#E2E2E2] box-border w-full pl-3 pr-10"
        />
      </div>
      <div className="flex flex-col relative">
        <label htmlFor="to-input" className="block text-sm font-medium text-black mb-4 mt-8">
          To
        </label>
        <div className="relative">
          <InputText
            id="to-input"
            value={tabSearchItems[TAB_KEYS.NEW_NOTIFICATION]?.name || ""}
            placeholder="Search recipient..."
            className="border rounded border-solid border-[#E2E2E2] box-border w-full pl-3 pr-10 cursor-pointer"
            onClick={handleSearchClick}
            onKeyDown={handleInputTextKeyDown}
            readOnly
          />
          <button
            className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-transparent border-none p-0 cursor-pointer"
            onClick={handleSearchClick}
            onKeyDown={handleSearchKeyDown}
            aria-label="Search patient"
            type="button"
          >
            <img
              src={search}
              alt=""
              aria-hidden="true"
              className="w-4 h-4"
            />
          </button>
        </div>
      </div>
      <div className="flex flex-col">
        <label htmlFor="subject-input" className="block text-sm font-medium text-black mb-4 mt-8">
          Subject
        </label>
        <InputText
          id="subject-input"
          value={subject}
          placeholder="Subject...."
          onChange={(e) => setSubject(e.target.value)}
          className="border rounded border-solid border-[#E2E2E2] box-border w-full pl-3 pr-10"
          maxLength={200}
        />
      </div>
      <div className="flex flex-col">
        <label htmlFor="message-input" className="block text-sm font-medium text-black mb-4 mt-8">
          Message
        </label>
        <InputTextarea
          id="message-input"
          value={body}
          placeholder="Type Here ......"
          rows={10}
          cols={30}
          onChange={(e) => setBody(e.target.value)}
          className="border rounded border-solid border-[#E2E2E2] box-border w-full pl-3 pr-10"
          maxLength={5000}
        />
      </div>
      <button
        className="w-full h-10 border border-[#004172] bg-[#004172] text-white font-medium rounded-md mt-6 hover:bg-[#00315a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={sendNotificationHandler}
        disabled={!tabSearchItems[TAB_KEYS.NEW_NOTIFICATION]?.code || !subject.trim() || !body.trim() || loading}
      >
        {loading ? "Sending..." : "Send Notification"}
      </button>
    </>
  ), [toName, tabSearchItems, subject, body, handleSearchClick, handleInputTextKeyDown, handleSearchKeyDown, sendNotificationHandler, loading]);

  return (
    <>
      <Toast ref={toastRef} />
      <Sidebar
        visible={show}
        position="right"
        onHide={() => {
          setShow(false);
          setFromdate(null);
          setTodate(null);
          setTabSearchItems({
            [TAB_KEYS.RECEIVED]: null,
            [TAB_KEYS.SENT]: null,
            [TAB_KEYS.NEW_NOTIFICATION]: null,
          });
        }}
        className="w-[500px]"
      >
        <h2 className="font-bold text-2xl leading-6 tracking-normal text-left text-[#262626] mb-6">
          Notifications
        </h2>

        <TabView
          className="mt-4"
          activeIndex={activeIndex}
          onTabChange={onTabChange}
        >
          <TabPanel header="Received">
            {renderReceivedTab()}
          </TabPanel>
          <TabPanel header="Sent">
            {renderSentTab()}
          </TabPanel>
          <TabPanel header="New Notification">
            {renderNewNotificationTab()}
          </TabPanel>
        </TabView>
      </Sidebar>
      
      <PatientSearch
        showbar={patientSearch}
        setshowBar={togglePatientSearchbar}
        onSelect={handleSelectedData}
      />
    </>
  );
};

Notifications.propTypes = {
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired
};

export default Notifications;